/**
 * Analítica de captación (F3-I) para admin.metrics: funnel de leads por etapa,
 * tasa de conversión y desglose por origen. Barras simples consistentes con las
 * tarjetas de métricas existentes.
 */
import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Contact } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { STAGES, useFunnel, useSources } from "./api";
import { SOURCE_LABEL, STAGE_LABEL } from "./labels";

const pct = (n: number, max: number): string => `${Math.round((n / max) * 100)}%`;

export function CrmFunnelSection() {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const funnel = useFunnel(activeOrgId);
  const sources = useSources(activeOrgId);

  const stageCount = (stage: string): number =>
    funnel.data?.byStage.find((s) => s.stage === stage)?.count ?? 0;
  const maxStage = (): number => Math.max(1, ...(funnel.data?.byStage.map((s) => s.count) ?? [1]));
  const maxSource = (): number => Math.max(1, ...(sources.data?.map((s) => s.total) ?? [1]));
  const conversionPct = (): number => Math.round((funnel.data?.conversionRate ?? 0) * 100);

  return (
    <Show when={activeOrgId()}>
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <Contact class="h-6 w-6 text-primary" />
          <div>
            <h3 class="text-xl font-bold tracking-tight">{t(msg`Lead capture`)}</h3>
            <p class="text-sm text-muted-foreground">
              {t(msg`Pipeline funnel and sources for the active academy.`)}
            </p>
          </div>
        </div>

        <Show when={!funnel.isLoading} fallback={<Skeleton class="h-40 w-full rounded-xl" />}>
          <div class="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent class="space-y-3 p-5">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-semibold">{t(msg`Funnel by stage`)}</span>
                  <span class="text-sm text-muted-foreground">
                    {t(msg`Conversion`)}: {conversionPct()}%
                  </span>
                </div>
                <For each={STAGES}>
                  {(stage) => (
                    <div>
                      <div class="mb-0.5 flex justify-between text-xs">
                        <span>{t(STAGE_LABEL[stage])}</span>
                        <span class="text-muted-foreground">{stageCount(stage)}</span>
                      </div>
                      <div class="h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          class="h-full rounded-full bg-accent"
                          style={{ width: pct(stageCount(stage), maxStage()) }}
                        />
                      </div>
                    </div>
                  )}
                </For>
              </CardContent>
            </Card>

            <Card>
              <CardContent class="space-y-3 p-5">
                <span class="text-sm font-semibold">{t(msg`Leads by source`)}</span>
                <For
                  each={sources.data ?? []}
                  fallback={<p class="text-sm text-muted-foreground">{t(msg`No leads yet.`)}</p>}
                >
                  {(s) => (
                    <div>
                      <div class="mb-0.5 flex justify-between text-xs">
                        <span>{t(SOURCE_LABEL[s.source])}</span>
                        <span class="text-muted-foreground">
                          {s.total} · {s.converted} {t(msg`enrolled`)}
                        </span>
                      </div>
                      <div class="h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          class="h-full rounded-full bg-primary"
                          style={{ width: pct(s.total, maxSource()) }}
                        />
                      </div>
                    </div>
                  )}
                </For>
              </CardContent>
            </Card>
          </div>
        </Show>
      </div>
    </Show>
  );
}
